import React from "react";
import { Mail, MessageSquare, Clock, HelpCircle, Send, CheckCircle } from "lucide-react";
import { Button } from "../Components/ui/button";
import { Input } from "../Components/ui/input";
import { Textarea } from "../Components/ui/textarea";
import { Card } from "../Components/ui/card";
import { base44 } from "../api/base44Client";

export default function Support() {
  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  React.useEffect(() => {
    const handleStorage = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('storage', handleStorage);
    
    const handleThemeChange = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('themechange', handleThemeChange);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('themechange', handleThemeChange);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Save to database
      await base44.entities.ContactSubmission.create({
        ...formData,
        type: 'support_ticket'
      });

      // Send to Google Sheets via webhook (you'll need to set this up)
      await fetch('https://script.google.com/macros/s/YOUR_SCRIPT_ID/exec', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          type: 'support_ticket',
          timestamp: new Date().toISOString()
        })
      });

      setSubmitted(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
      
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      alert('There was an error submitting your support ticket. Please try again or email us directly at problem@sunscreened.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-orange-50 via-white to-coral-50'
    }`}>
      {/* Header */}
      <div className={`relative overflow-hidden ${
        theme === 'dark'
          ? 'bg-gradient-to-br from-orange-600 via-coral-600 to-pink-600'
          : 'bg-gradient-to-br from-orange-500 via-coral-500 to-pink-500'
      } text-white py-20 md:py-32`}>
        <div className="absolute inset-0">
          <div className="absolute top-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Support Center
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Need help? We're here for you. Submit a support ticket and we'll get back to you in less than 24 hours.
          </p>
        </div>
      </div>

      {/* Support Form Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Left side - Info */}
          <div>
            <h2 className={`text-3xl font-bold mb-6 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              How Can We Help?
            </h2>
            <p className={`text-lg leading-relaxed mb-8 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Having issues with the app? Need technical assistance? Our support team is ready to help you resolve any problems quickly and efficiently.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  theme === 'dark' 
                    ? 'bg-orange-500/20 text-orange-400' 
                    : 'bg-orange-100 text-orange-600'
                }`}>
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className={`font-semibold mb-1 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Email Support
                  </h3>
                  <a 
                    href="mailto:problem@sunscreened.com"
                    className={`text-orange-500 hover:text-orange-600 ${
                      theme === 'dark' ? 'hover:text-orange-400' : ''
                    }`}
                  >
                    problem@sunscreened.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  theme === 'dark' 
                    ? 'bg-coral-500/20 text-coral-400' 
                    : 'bg-coral-100 text-coral-600'
                }`}>
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <h3 className={`font-semibold mb-1 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Response Time
                  </h3>
                  <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                    We guarantee a response in less than 24 hours
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  theme === 'dark' 
                    ? 'bg-pink-500/20 text-pink-400' 
                    : 'bg-pink-100 text-pink-600'
                }`}>
                  <MessageSquare className="w-6 h-6" />
                </div>
                <div>
                  <h3 className={`font-semibold mb-1 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Support Ticket
                  </h3>
                  <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                    Submit a ticket using the form and we'll track your issue
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Form */}
          <div>
            {submitted ? (
              <Card className={`p-8 text-center ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-200'
              }`}>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className={`text-2xl font-bold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Ticket Submitted!
                </h3>
                <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                  We've received your support ticket and will respond within 24 hours.
                </p>
                <p className={`mt-4 text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  You can also reach us directly at{' '}
                  <a 
                    href="mailto:problem@sunscreened.com"
                    className="text-orange-500 hover:text-orange-600"
                  >
                    problem@sunscreened.com
                  </a>
                </p>
              </Card>
            ) : (
              <Card className={`p-8 ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-200'
              }`}>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Your Name
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      className={theme === 'dark' ? 'bg-gray-900 border-gray-600 text-white' : ''}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Email Address
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      className={theme === 'dark' ? 'bg-gray-900 border-gray-600 text-white' : ''}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Subject
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Brief description of your issue"
                      className={theme === 'dark' ? 'bg-gray-900 border-gray-600 text-white' : ''}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Message
                    </label>
                    <Textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Please describe your issue in detail..."
                      rows={5}
                      className={theme === 'dark' ? 'bg-gray-900 border-gray-600 text-white' : ''}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-orange-500 to-coral-500 hover:from-orange-600 hover:to-coral-600 text-white"
                  >
                    {loading ? (
                      "Submitting..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Support Ticket
                      </>
                    )}
                  </Button>
                </form>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

